/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, BoxView, Text, Icon } from '@components/atoms'
import {
	InquiryCard,
	InquiryTimelineDrawer,
	InquiryReplyDialog,
} from '@components/organisms'
import {
	Tabs,
	Tab,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	InputAdornment,
	IconButton,
} from '@mui/material'
import { useDebouncedCallback } from 'use-debounce'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'
// 패키지 루트('react-virtualized')를 쓰면 webpack 5 가 WindowScroller 의 깨진 export 로
// 컴파일 오류를 낸다. 쓰는 것만 commonjs 경로로 받는다.
import { AutoSizer } from 'react-virtualized/dist/commonjs/AutoSizer'
import { List, ListRowProps } from 'react-virtualized/dist/commonjs/List'
import {
	CellMeasurer,
	CellMeasurerCache,
} from 'react-virtualized/dist/commonjs/CellMeasurer'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import {
	useAppDispatch,
	useAppSelector,
	inquiryMgmtActions,
	configActions,
	ApiGetDataStatus,
} from '@redux/index'
import {
	InquiryType,
	InquiryCommentType,
	InquiryStatusLogType,
	InquiryStatusType,
	UserType,
} from '@schemas'
import { INQUIRY_FILTER_OPTIONS } from '@configs'
import { inquiryMgmtApi } from '@api'
import { downloadBase64File } from '@components/organisms/inquiry/replyHtml'

dayjs.extend(utc)
dayjs.extend(timezone)

/** 끝에서 이만큼 남았을 때 다음 페이지를 미리 받는다 */
const LOAD_MORE_THRESHOLD = 6
/** 아직 안 잰 카드 한 장의 추정 높이(px). 실측 평균에 가까워야 스크롤바 길이가 안 출렁인다 */
const ESTIMATED_ROW_HEIGHT = 170
/** 스크롤이 바닥에서 이 px 안이면 다음 페이지 — 스크롤바를 끝까지 끌어내린 경우 */
const LOAD_MORE_BOTTOM_PX = 400

/**
 * 문의 게시판 관리 — 한 화면.
 * 카드 목록(가상 스크롤 + 무한 로드) + 오른쪽 타임라인 드로어.
 * 상세 페이지(/main/inquiry/:id)에 있던 기능은 전부 카드와 드로어로 옮겼다.
 */
const InquiryManagementPage = () => {
	const reduxDispatch = useAppDispatch()

	const inquiryList: InquiryType[] = useAppSelector(
		state => state.inquiryMgmt.inquiryList,
	)
	const listStatus: ApiGetDataStatus = useAppSelector(
		state => state.inquiryMgmt.inquiryListStatus,
	)
	const hasMore: boolean = useAppSelector(state => state.inquiryMgmt.hasMore)
	const isLoadingMore: boolean = useAppSelector(state => state.inquiryMgmt.isLoadingMore)
	const statusFilter: InquiryStatusType | 'all' = useAppSelector(
		state => state.inquiryMgmt.statusFilter,
	)
	const searchQuery: string = useAppSelector(state => state.inquiryMgmt.searchQuery)
	const userList: { id: string; name: string }[] = useAppSelector(
		state => state.inquiryMgmt.userList,
	)
	const selectedInquiry: InquiryType | null = useAppSelector(
		state => state.inquiryMgmt.selectedInquiry,
	)
	const detailStatus: ApiGetDataStatus = useAppSelector(
		state => state.inquiryMgmt.selectedInquiryStatus,
	)
	const isTimelineOpen: boolean = useAppSelector(
		state => state.inquiryMgmt.isTimelineOpen,
	)
	const comments: InquiryCommentType[] = useAppSelector(
		state => state.inquiryMgmt.comments,
	)
	const statusLogs: InquiryStatusLogType[] = useAppSelector(
		state => state.inquiryMgmt.statusLogs,
	)
	const loginUser: UserType = useAppSelector(state => state.user.user)

	// 카드별 화면 상태는 페이지가 든다 — 가상 리스트가 카드를 내렸다 올려도 남는다
	const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({})
	const [translations, setTranslations] = useState<Record<string, string>>({})
	const [deleteTargetId, setDeleteTargetId] = useState('')
	const [replyTargetId, setReplyTargetId] = useState('')
	// 입력란 값은 즉시, 검색 요청은 300ms 뒤에 (한 글자마다 서버를 때리지 않는다)
	const [searchInput, setSearchInput] = useState(searchQuery)

	// 콜백을 고정하기 위한 최신값 참조 (카드는 React.memo)
	const listDataRef = useRef<InquiryType[]>(inquiryList)
	listDataRef.current = inquiryList
	const drawerRef = useRef({
		isOpen: isTimelineOpen,
		selectedId: selectedInquiry?.id ?? '',
	})
	drawerRef.current = { isOpen: isTimelineOpen, selectedId: selectedInquiry?.id ?? '' }
	const loadMoreRef = useRef({ hasMore, isLoadingMore })
	loadMoreRef.current = { hasMore, isLoadingMore }
	const statusFilterRef = useRef(statusFilter)
	statusFilterRef.current = statusFilter

	const listRef = useRef<List | null>(null)
	const cache = useMemo(
		() =>
			new CellMeasurerCache({
				fixedWidth: true,
				defaultHeight: ESTIMATED_ROW_HEIGHT,
				// id 로 캐시하면 행이 빠지거나 뒤에 붙어도 잰 높이가 그대로 맞는다
				keyMapper: rowIndex => listDataRef.current[rowIndex]?.id ?? '__footer__',
			}),
		[],
	)

	useEffect(() => {
		reduxDispatch(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
	}, [reduxDispatch])

	// 행이 빠지거나 붙으면 위치를 다시 계산한다 (높이는 id 캐시에서 그대로)
	useEffect(() => {
		listRef.current?.recomputeRowHeights()
	}, [inquiryList])

	/** 탭·검색어가 바뀌면 첫 페이지부터 다시 */
	const reloadFromTop = useCallback(() => {
		reduxDispatch(inquiryMgmtActions.TRIGGER_INIT_INQUIRY_MGMT(true))
		cache.clearAll()
		listRef.current?.scrollToPosition(0)
	}, [reduxDispatch, cache])

	const onTabChange = useCallback(
		(_: React.SyntheticEvent, newValue: InquiryStatusType | 'all') => {
			reduxDispatch(inquiryMgmtActions.STATUS_FILTER(newValue))
			reloadFromTop()
		},
		[reduxDispatch, reloadFromTop],
	)

	const commitSearch = useDebouncedCallback((value: string) => {
		const next = value.trim()
		if (next === searchQuery) return
		reduxDispatch(inquiryMgmtActions.SEARCH_QUERY(next))
		reloadFromTop()
	}, 300)

	const onSearchChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setSearchInput(e.target.value)
			commitSearch(e.target.value)
		},
		[commitSearch],
	)

	const onSearchClear = useCallback(() => {
		setSearchInput('')
		commitSearch.cancel()
		if (searchQuery !== '') {
			reduxDispatch(inquiryMgmtActions.SEARCH_QUERY(''))
			reloadFromTop()
		}
	}, [commitSearch, searchQuery, reduxDispatch, reloadFromTop])

	const onSearchKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === 'Enter') commitSearch.flush()
			if (e.key === 'Escape') onSearchClear()
		},
		[commitSearch, onSearchClear],
	)

	const onResize = useCallback(() => {
		// 폭이 바뀌면(드로어 열림·창 크기) 줄바꿈이 달라져 높이를 전부 다시 잰다
		cache.clearAll()
		listRef.current?.recomputeRowHeights()
	}, [cache])

	const loadMore = useCallback(() => {
		const { hasMore: more, isLoadingMore: loading } = loadMoreRef.current
		if (!more || loading) return
		reduxDispatch(inquiryMgmtActions.TRIGGER_LOAD_MORE_INQUIRIES(true))
	}, [reduxDispatch])

	// 끝 근처 카드가 그려지면
	const onRowsRendered = useCallback(
		({ stopIndex }: { stopIndex: number }) => {
			if (stopIndex >= listDataRef.current.length - LOAD_MORE_THRESHOLD) loadMore()
		},
		[loadMore],
	)

	// 스크롤이 바닥 근처면 (인덱스와 무관하게 — 스크롤바를 단번에 끌어내린 경우)
	const onScroll = useCallback(
		({
			clientHeight,
			scrollHeight,
			scrollTop,
		}: {
			clientHeight: number
			scrollHeight: number
			scrollTop: number
		}) => {
			if (scrollTop + clientHeight >= scrollHeight - LOAD_MORE_BOTTOM_PX) loadMore()
		},
		[loadMore],
	)

	// ── 드로어 ──
	const showTimeline = useCallback(
		(inquiryId: string) => {
			const inquiry = listDataRef.current.find(anItem => anItem.id === inquiryId)
			if (!inquiry) return
			if (drawerRef.current.selectedId !== inquiryId) {
				reduxDispatch(inquiryMgmtActions.COMMENTS([]))
				reduxDispatch(inquiryMgmtActions.STATUS_LOGS([]))
			}
			reduxDispatch(inquiryMgmtActions.SELECTED_INQUIRY(inquiry))
			reduxDispatch(inquiryMgmtActions.IS_TIMELINE_OPEN(true))
			reduxDispatch(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
		},
		[reduxDispatch],
	)

	const onCloseTimeline = useCallback(() => {
		reduxDispatch(inquiryMgmtActions.IS_TIMELINE_OPEN(false))
		reduxDispatch(inquiryMgmtActions.SELECTED_INQUIRY(null))
		reduxDispatch(inquiryMgmtActions.COMMENTS([]))
		reduxDispatch(inquiryMgmtActions.STATUS_LOGS([]))
	}, [reduxDispatch])

	/** 카드의 [타임라인] — 다른 카드면 그 카드로 바꾸고, 이미 보고 있는 카드면 닫는다 */
	const onTimelineButton = useCallback(
		(inquiryId: string) => {
			if (drawerRef.current.isOpen && drawerRef.current.selectedId === inquiryId) {
				onCloseTimeline()
				return
			}
			showTimeline(inquiryId)
		},
		[onCloseTimeline, showTimeline],
	)

	// ── 카드 ──
	const onToggleExpand = useCallback((inquiryId: string) => {
		setExpandedMap(prev => ({ ...prev, [inquiryId]: !prev[inquiryId] }))
	}, [])

	const onCardClick = useCallback(
		(inquiryId: string) => {
			onToggleExpand(inquiryId)
			// 드로어가 열려 있으면 이 카드로 바꾼다
			if (drawerRef.current.isOpen && drawerRef.current.selectedId !== inquiryId) {
				showTimeline(inquiryId)
			}
		},
		[onToggleExpand, showTimeline],
	)

	const onStatusChange = useCallback(
		(inquiryId: string, status: string) => {
			const inquiry = listDataRef.current.find(anItem => anItem.id === inquiryId)
			if (!inquiry || !status || status === inquiry.status) return
			reduxDispatch(
				inquiryMgmtActions.UPDATE_STATUS_PAYLOAD({ inquiryId, status, note: '' }),
			)
			reduxDispatch(inquiryMgmtActions.TRIGGER_UPDATE_STATUS(true))
		},
		[reduxDispatch],
	)

	const onHandlerChange = useCallback(
		(inquiryId: string, handlerId: string) => {
			reduxDispatch(inquiryMgmtActions.UPDATE_HANDLER_PAYLOAD({ inquiryId, handlerId }))
			reduxDispatch(inquiryMgmtActions.TRIGGER_UPDATE_HANDLER(true))
		},
		[reduxDispatch],
	)

	const onAskDelete = useCallback((inquiryId: string) => {
		setDeleteTargetId(inquiryId)
	}, [])

	const onConfirmDelete = useCallback(() => {
		if (!deleteTargetId) return
		reduxDispatch(inquiryMgmtActions.DELETE_INQUIRY_ID(deleteTargetId))
		reduxDispatch(inquiryMgmtActions.TRIGGER_DELETE_INQUIRY(true))
		setDeleteTargetId('')
	}, [deleteTargetId, reduxDispatch])

	// ── 이메일 답변 ──
	const onOpenReply = useCallback((inquiryId: string) => {
		setReplyTargetId(inquiryId)
	}, [])

	const onCloseReply = useCallback(() => {
		setReplyTargetId('')
	}, [])

	const onReplySent = useCallback(
		(inquiryId: string, nextStatus: string) => {
			setReplyTargetId('')
			reduxDispatch(
				configActions.TOAST({ isShow: true, message: '답변 메일을 보냈습니다.' }),
			)
			const statusFilter = statusFilterRef.current
			if (statusFilter !== 'all' && statusFilter !== nextStatus) {
				reduxDispatch(inquiryMgmtActions.REMOVE_INQUIRY(inquiryId))
			} else {
				reduxDispatch(
					inquiryMgmtActions.PATCH_INQUIRY({
						id: inquiryId,
						// 마지막 공개 코멘트가 이제 스태프 답변 — 「고객 답글」 표시를 끈다
						changes: { status: nextStatus, lastPublicKind: 'email' },
					}),
				)
			}
			if (drawerRef.current.isOpen && drawerRef.current.selectedId === inquiryId) {
				reduxDispatch(inquiryMgmtActions.TRIGGER_LOAD_INQUIRY_DETAIL(true))
			}
		},
		[reduxDispatch],
	)

	// ── 답변 첨부 받기 (S3 비공개 → base64) ──
	const onDownloadFile = useCallback(
		async (inquiryId: string, fileId: string) => {
			const res = await inquiryMgmtApi.getInquiryReplyFile({
				params: { inquiryId: inquiryId, fileId: fileId },
			})
			const file = res && res.status === 200 ? (res.data as any[])[0] : undefined
			if (!file) {
				reduxDispatch(
					configActions.TOAST({ isShow: true, message: '첨부를 받지 못했습니다.' }),
				)
				return
			}
			downloadBase64File(file)
		},
		[reduxDispatch],
	)

	const onTranslated = useCallback((inquiryId: string, text: string) => {
		setTranslations(prev => ({ ...prev, [inquiryId]: text }))
	}, [])

	const onAddComment = useCallback(
		(inquiryId: string, body: string) => {
			reduxDispatch(
				inquiryMgmtActions.ADD_COMMENT_PAYLOAD({ inquiryId, body, isInternal: true }),
			)
			reduxDispatch(inquiryMgmtActions.TRIGGER_ADD_COMMENT(true))
		},
		[reduxDispatch],
	)

	const onDeleteComment = useCallback(
		(inquiryId: string, commentId: string) => {
			reduxDispatch(inquiryMgmtActions.DELETE_COMMENT_PAYLOAD({ inquiryId, commentId }))
			reduxDispatch(inquiryMgmtActions.TRIGGER_DELETE_COMMENT(true))
		},
		[reduxDispatch],
	)

	// ── 가상 리스트 행 ──
	const selectedId = isTimelineOpen ? selectedInquiry?.id ?? '' : ''
	const rowCount = inquiryList.length + 1 // 마지막 행은 로딩/끝 표시

	const rowRenderer = ({ index, key, parent, style: rowStyle }: ListRowProps) => {
		const inquiry: InquiryType | undefined = inquiryList[index]
		return (
			<CellMeasurer
				cache={cache}
				columnIndex={0}
				key={key}
				parent={parent}
				rowIndex={index}
			>
				{({ measure, registerChild }) => (
					<div ref={el => registerChild?.(el ?? undefined)} style={rowStyle}>
						{inquiry ? (
							<View className="rowInner">
								<InquiryCard
									inquiry={inquiry}
									userList={userList}
									isSelected={inquiry.id === selectedId}
									isExpanded={!!expandedMap[inquiry.id]}
									translated={translations[inquiry.id] ?? ''}
									onCardClick={onCardClick}
									onToggleExpand={onToggleExpand}
									onOpenTimeline={onTimelineButton}
									onReply={onOpenReply}
									onStatusChange={onStatusChange}
									onHandlerChange={onHandlerChange}
									onDelete={onAskDelete}
									onTranslated={onTranslated}
									onMeasure={measure}
								/>
							</View>
						) : (
							<View className="footerRow">
								{hasMore || isLoadingMore ? (
									<CircularProgress size={22} />
								) : (
									<Text category="c1" hint="500">
										{`${inquiryList.length}건 · 끝`}
									</Text>
								)}
							</View>
						)}
					</div>
				)}
			</CellMeasurer>
		)
	}

	return (
		<View css={style}>
			<BoxView
				title="문의 게시판 관리"
				rightHeaderAccessory={
					<Text category="c1" hint="600">
						{listStatus === 'loading'
							? ''
							: `${inquiryList.length}건${hasMore ? '+' : ''}`}
					</Text>
				}
			>
				<View className="contentView">
					<View className="listCol">
						<View className="tabsView">
							<Tabs value={statusFilter} onChange={onTabChange}>
								{INQUIRY_FILTER_OPTIONS.map(opt => (
									<Tab key={opt.value} value={opt.value} label={opt.label} />
								))}
							</Tabs>
						</View>

						<View className="searchView">
							<TextField
								value={searchInput}
								onChange={onSearchChange}
								onKeyDown={onSearchKeyDown}
								placeholder={`${
									INQUIRY_FILTER_OPTIONS.find(opt => opt.value === statusFilter)?.label ??
									'전체'
								} 안에서 이름 · 이메일 · 담당자 · 내용 검색`}
								size="small"
								fullWidth
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Icon name="magnify" style={{ fontSize: 18, color: mono.faint }} />
										</InputAdornment>
									),
									endAdornment: searchInput ? (
										<InputAdornment position="end">
											<IconButton size="small" onClick={onSearchClear} title="지우기">
												<Icon
													name="close-circle"
													style={{ fontSize: 16, color: mono.faint }}
												/>
											</IconButton>
										</InputAdornment>
									) : null,
								}}
							/>
						</View>

						{listStatus === 'loading' ? (
							<View className="loadingView">
								<CircularProgress size={28} />
							</View>
						) : listStatus === 'empty' || (inquiryList.length === 0 && !hasMore) ? (
							<View className="loadingView">
								<Text category="p2" hint="400">
									{searchQuery
										? `'${searchQuery}' 검색 결과가 없습니다.`
										: '문의가 없습니다.'}
								</Text>
							</View>
						) : (
							<View className="listView">
								<AutoSizer onResize={onResize}>
									{({ width, height }) => (
										<List
											ref={listRef}
											width={width}
											height={height}
											rowCount={rowCount}
											rowHeight={cache.rowHeight}
											deferredMeasurementCache={cache}
											rowRenderer={rowRenderer}
											onRowsRendered={onRowsRendered}
											onScroll={onScroll}
											estimatedRowSize={ESTIMATED_ROW_HEIGHT}
											overscanRowCount={4}
											style={{ outline: 'none' }}
											// memo 카드가 새 값을 받도록 — List 는 이 props 가 바뀔 때만 다시 그린다
											expandedMap={expandedMap}
											translations={translations}
											selectedId={selectedId}
											userList={userList}
											hasMore={hasMore}
											isLoadingMore={isLoadingMore}
										/>
									)}
								</AutoSizer>
							</View>
						)}
					</View>

					<InquiryTimelineDrawer
						open={isTimelineOpen}
						inquiry={selectedInquiry}
						detailStatus={detailStatus}
						comments={comments}
						statusLogs={statusLogs}
						userList={userList}
						onClose={onCloseTimeline}
						onAddComment={onAddComment}
						onDeleteComment={onDeleteComment}
						onReply={onOpenReply}
						onDownloadFile={(inquiryId, fileId) => void onDownloadFile(inquiryId, fileId)}
					/>
				</View>
			</BoxView>

			<InquiryReplyDialog
				open={replyTargetId !== ''}
				inquiry={inquiryList.find(anItem => anItem.id === replyTargetId) ?? null}
				sender={loginUser}
				onClose={onCloseReply}
				onSent={onReplySent}
			/>

			<Dialog
				open={deleteTargetId !== ''}
				onClose={() => setDeleteTargetId('')}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>문의 삭제</DialogTitle>
				<DialogContent>
					<Text category="p2">정말 이 문의를 삭제하시겠습니까?</Text>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteTargetId('')}>취소</Button>
					<Button variant="outlined" onClick={onConfirmDelete}>
						삭제
					</Button>
				</DialogActions>
			</Dialog>
		</View>
	)
}

export default InquiryManagementPage

const style = css({
	...boShared,
	width: '100%',
	height: '100%',

	'.contentView': {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		height: '100%',
		minHeight: 0,
	},
	'.listCol': {
		flex: 1,
		minWidth: 0,
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '0.75rem',
		paddingRight: '0.75rem',
	},
	'.tabsView': {
		borderBottom: `1px solid ${mono.line}`,
		flexShrink: 0,
		'.MuiTabs-root': { minHeight: 40 },
	},
	'.searchView': {
		flexShrink: 0,
		'.MuiInputBase-root': { fontSize: 13, background: mono.surface },
	},
	'.loadingView': {
		display: 'flex',
		justifyContent: 'center',
		padding: '3rem 0',
	},
	'.listView': {
		flex: 1,
		minHeight: 0,
		width: '100%',
	},
	'.rowInner': {
		paddingBottom: '0.75rem',
		paddingRight: '0.25rem',
	},
	'.footerRow': {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: '1rem 0 1.5rem',
	},

	'@media (max-width: 900px)': {
		'.contentView': {
			height: ['calc(100vh - 8rem)', 'calc(100dvh - 8rem)'],
		},
		'.listCol': {
			paddingRight: 0,
		},
	},
})
